// src/components/Contract/ST124Form.tsx
//
// Pure-HTML/CSS rendering of NY State form ST-124, "Certificate of Capital
// Improvement". Prints directly via window.print() — no pdf-lib, no template
// overlay. Layout mirrors the field map of the official AcroForm (see
// public/forms/st124_fill.pdf), but the visual recreation is plain HTML.
//
// All values are display-only; data entry happens on the Contract → Edit tab
// and flows into the parent's st124Data useMemo.

import { NY_STATE_LOGO_DATA_URI } from "../../Assets/nyStateLogo";
import "./ST124Form.css";
import SignatureImage from "./SignatureImage";
import InlineEditText from "../InlineEditText";

export interface ST124FormProps {
  contractor: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  customer: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    salesTaxCertNumber?: string;
  };
  description: string;
  /** Given → the description is edited where it sits on the form (click to
   *  edit, like the proposal page). Omitted → plain read-only rows. */
  onDescriptionChange?: (next: string) => void;
  descriptionPlaceholder?: string;
  project: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  isOwner: boolean;
  contractIncludesItems: boolean;
  customerSignature: { title: string; date: string };
  contractorSignature: { imageUrl?: string; title: string; date: string };
}

export const MAX_DESC_LINE = 95;
export const MAX_DESC_LINES = 4;

/** Word-wrap the description into lines of at most MAX_DESC_LINE chars.
 *  Returns the raw wrapped lines with NO padding or slicing, so callers
 *  (e.g. the editor's overflow warning) can tell how many lines the text
 *  would actually occupy on the printed form. */
export function wrapDescriptionLines(text: string): string[] {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    const candidate = cur ? `${cur} ${w}` : w;
    if (candidate.length <= MAX_DESC_LINE) {
      cur = candidate;
    } else {
      if (cur) lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

/** The printed shape: exactly MAX_DESC_LINES rows (padded with blanks,
 *  truncated if longer) so the fixed 4-row form block never overflows. */
function wrapDescription(text: string): string[] {
  const lines = wrapDescriptionLines(text);
  while (lines.length < MAX_DESC_LINES) lines.push("");
  return lines.slice(0, MAX_DESC_LINES);
}

function Check({ on }: { on: boolean }) {
  return <span className="st124-check">{on ? "☒" : "☐"}</span>;
}

export function ST124Form(props: ST124FormProps) {
  const descLines = wrapDescription(props.description);

  return (
    <article className="st124-form st124-print">
      <header className="st124-header">
        <img
          src={NY_STATE_LOGO_DATA_URI}
          alt="New York State"
          className="st124-header-logo"
        />
        <div className="st124-header-text">
          <div className="st124-dept">Department of Taxation and Finance</div>
          <div className="st124-subdept">
            New York State and Local Sales and Use Tax
          </div>
        </div>
        <div>
          <div className="st124-header-num">ST-124</div>
          <div className="st124-header-num-rev">(12/15)</div>
        </div>
      </header>
      <h1 className="st124-title">Certificate of Capital Improvement</h1>

      <p className="st124-intro">
        After this certificate is completed and signed by both the customer and
        the contractor, the contractor must keep it in its records as evidence
        that the work performed is a capital improvement and is not subject to
        New York State sales tax on labor.{" "}
        <strong>Do not file this form with the Tax Department.</strong>
      </p>

      <table className="st124-info">
        <tbody>
          <tr>
            <td>
              <span className="st124-label">Name of contractor</span>
              <span className="st124-value">{props.contractor.name}</span>
            </td>
            <td>
              <span className="st124-label">Name of customer</span>
              <span className="st124-value">{props.customer.name}</span>
            </td>
          </tr>
          <tr>
            <td>
              <span className="st124-label">Address (number and street)</span>
              <span className="st124-value">{props.contractor.address}</span>
            </td>
            <td>
              <span className="st124-label">Address (number and street)</span>
              <span className="st124-value">{props.customer.address}</span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="st124-cityrow">
                <div className="st124-cell st124-cell-grow">
                  <span className="st124-label">City</span>
                  <span className="st124-value">{props.contractor.city}</span>
                </div>
                <div className="st124-cell st124-cell-state">
                  <span className="st124-label">State</span>
                  <span className="st124-value">{props.contractor.state}</span>
                </div>
                <div className="st124-cell st124-cell-zip">
                  <span className="st124-label">ZIP code</span>
                  <span className="st124-value">{props.contractor.zip}</span>
                </div>
              </div>
            </td>
            <td>
              <div className="st124-cityrow">
                <div className="st124-cell st124-cell-grow">
                  <span className="st124-label">City</span>
                  <span className="st124-value">{props.customer.city}</span>
                </div>
                <div className="st124-cell st124-cell-state">
                  <span className="st124-label">State</span>
                  <span className="st124-value">{props.customer.state}</span>
                </div>
                <div className="st124-cell st124-cell-zip">
                  <span className="st124-label">ZIP code</span>
                  <span className="st124-value">{props.customer.zip}</span>
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td />
            <td>
              <span className="st124-label">
                Sales tax Certificate of Authority number (if applicable)
              </span>
              <span className="st124-value">
                {props.customer.salesTaxCertNumber || ""}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="st124-section">
        To be completed by the customer (purchaser of services)
      </h2>

      <div className="st124-desc">
        <div className="st124-desc-label">
          Describe capital improvement to be performed:
        </div>
        <InlineEditText
          value={props.description}
          onChange={(next) => props.onDescriptionChange?.(next)}
          editable={!!props.onDescriptionChange}
          className="st124-desc-lines"
          editorClassName="st124-desc-editor"
          label="Describe capital improvement"
          placeholder={props.descriptionPlaceholder}
        >
          {descLines.map((line, i) => (
            <div key={i} className="st124-desc-line">
              {line || " "}
            </div>
          ))}
        </InlineEditText>
      </div>

      <div className="st124-projectbox">
        <div className="st124-cell st124-cell-grow">
          <span className="st124-label">Project name</span>
          <span className="st124-value">{props.project.name}</span>
        </div>
      </div>

      <div className="st124-projectbox">
        <div className="st124-cell st124-cell-grow">
          <span className="st124-label">Street address (number and street)</span>
          <span className="st124-value">{props.project.address}</span>
        </div>
        <div className="st124-cell st124-cell-city">
          <span className="st124-label">City</span>
          <span className="st124-value">{props.project.city}</span>
        </div>
        <div className="st124-cell st124-cell-state">
          <span className="st124-label">State</span>
          <span className="st124-value">{props.project.state}</span>
        </div>
        <div className="st124-cell st124-cell-zip">
          <span className="st124-label">ZIP code</span>
          <span className="st124-value">{props.project.zip}</span>
        </div>
      </div>

      <p className="st124-certify-intro">
        <strong>I certify that:</strong>
      </p>
      <ol className="st124-bullets">
        <li>
          I am the <Check on={props.isOwner} /> owner{" "}
          <Check on={!props.isOwner} /> tenant of the real property identified
          above.
        </li>
        <li>
          The capital improvement described above will be performed on the
          property identified above and will be permanently affixed to the real
          property so that removal would cause material damage to the property
          or article itself.
        </li>
        <li>
          The contract for the improvement{" "}
          <Check on={props.contractIncludesItems} /> includes{" "}
          <Check on={!props.contractIncludesItems} /> does not include tangible
          personal property that will not become part of the real property.
        </li>
      </ol>

      <p className="st124-certify-intro">
        <strong>I understand that:</strong>
      </p>
      <ul className="st124-bullets">
        <li>
          The contractor must keep this certificate in its records as evidence
          that the work performed constitutes a capital improvement, and that
          no New York State sales tax was due on the labor portion of the
          contract.
        </li>
        <li>
          Issuing this certificate when I know or should know that the work is
          not a capital improvement is fraud and may subject me to civil and
          criminal penalties under the Tax Law.
        </li>
        <li>
          A capital improvement is defined in section 1101(b)(9) of the Tax Law
          as an addition or alteration to real property that (i) substantially
          adds to the value of the real property or appreciably prolongs its
          useful life; (ii) becomes part of the real property or is permanently
          affixed to it so that removal would cause material damage to the
          property or article itself; and (iii) is intended to become a
          permanent installation.
        </li>
      </ul>

      <div className="st124-sigrow">
        <div className="st124-sig st124-sig-wide">
          <div className="st124-sig-content" />
          <span className="st124-sig-label">Signature of customer</span>
        </div>
        <div className="st124-sig">
          <div className="st124-sig-content">
            <span className="st124-sig-text">
              {props.customerSignature.title}
            </span>
          </div>
          <span className="st124-sig-label">Title</span>
        </div>
        <div className="st124-sig">
          <div className="st124-sig-content">
            <span className="st124-sig-text">
              {props.customerSignature.date}
            </span>
          </div>
          <span className="st124-sig-label">Date</span>
        </div>
      </div>

      <h2 className="st124-section">To be completed by the contractor</h2>

      <p className="st124-contractor-paragraph">
        Based on information available to me, I certify that the tangible
        personal property being transferred to the customer in performance of
        this contract will be permanently affixed to the real property
        identified above so that removal would cause material damage to the
        property or article itself, and that the work performed under this
        contract constitutes a capital improvement to that real property within
        the meaning of section 1101(b)(9) of the Tax Law.
      </p>

      <div className="st124-sigrow">
        <div className="st124-sig st124-sig-wide st124-sig-image">
          <div className="st124-sig-content">
            <SignatureImage url={props.contractorSignature.imageUrl} />
          </div>
          <span className="st124-sig-label">Signature of contractor</span>
        </div>
        <div className="st124-sig">
          <div className="st124-sig-content">
            <span className="st124-sig-text">
              {props.contractorSignature.title}
            </span>
          </div>
          <span className="st124-sig-label">Title</span>
        </div>
        <div className="st124-sig">
          <div className="st124-sig-content">
            <span className="st124-sig-text">
              {props.contractorSignature.date}
            </span>
          </div>
          <span className="st124-sig-label">Date</span>
        </div>
      </div>

      <p className="st124-footer-note">
        <strong>
          This certificate is not valid unless all entries are completed.
        </strong>
      </p>
    </article>
  );
}
